import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import ProgressStepper from "@/components/ProgressStepper";
import UploadStep from "@/components/steps/UploadStep";
import StyleStep from "@/components/steps/StyleStep";
import GeneratingStep from "@/components/steps/GeneratingStep";
import PreviewStep from "@/components/steps/PreviewStep";
import GroomerCard from "@/components/steps/GroomerCard";
import ProUpgradeModal from "@/components/ProUpgradeModal";
import { generateGroomingPreview } from "@/lib/generateGroomingPreview";
import { loadSession, saveSession, clearSession } from "@/lib/session";
import { getUsage, refreshUsage, applyServerUsage, canGeneratePreview } from "@/lib/subscription";
import { saveDog, addHistoryEntry, updateHistoryEntry, findDogByFingerprint, getDog, sortHistory } from "@/lib/dogProfiles";
import { getStyleById } from "@/lib/groomingStyles";

const DEFAULT_CONFIG = {
  style: null,
  customInstructions: "",
  bodyLength: "",
  faceStyle: "",
  earStyle: "",
  tailStyle: "",
  dogName: "",
  breed: "",
  breedUnknown: false,
  age: "",
};

const ERROR_COPY = {
  INVALID_PHOTO: "Please upload a clear photo of your dog and try again.",
  GENERATION_FAILED: "Your preview could not be created. Your credit was not used. Please try again.",
  NETWORK: "We couldn't reach the studio. Check your connection and try again.",
  SERVER_ERROR: "Something went wrong. Please try again in a moment.",
};

// Builds wizard state from a saved dog (+ optional history entry) for "Reuse This Style".
function restoreFromDog(dog, entry) {
  const config = {
    ...DEFAULT_CONFIG,
    dogName: dog.name || "",
    breed: dog.breed || "",
    breedUnknown: !!dog.breedUnknown,
    age: dog.age || "",
  };
  if (entry) {
    Object.assign(config, {
      style: getStyleById(entry.styleId) || (entry.styleName ? { id: entry.styleId || "custom", name: entry.styleName } : null),
      bodyLength: entry.bodyLength || "",
      faceStyle: entry.faceStyle || "",
      earStyle: entry.earStyle || "",
      tailStyle: entry.tailStyle || "",
      customInstructions: entry.customInstructions || "",
    });
  }
  const photo = dog.originalPhoto ? { dataUrl: dog.originalPhoto, name: `${dog.name || "dog"}.jpg`, fromProfile: true } : null;
  return { config, photo };
}

export default function Studio() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("photo");
  const [photo, setPhoto] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [results, setResults] = useState([]); // every preview generated this session
  const [activeIdx, setActiveIdx] = useState(0);
  const [savedDogId, setSavedDogId] = useState(null);
  const [savedEntryId, setSavedEntryId] = useState(null);
  const [linkedDogId, setLinkedDogId] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("NO_CREDITS");
  const [usage, setUsage] = useState(() => getUsage());

  const preview = results[activeIdx] || null;

  // Surface the current dog's favourite look (if any) for one-tap reuse in the Style step.
  const favoriteLook = useMemo(() => {
    const dog =
      (linkedDogId && getDog(linkedDogId)) ||
      findDogByFingerprint({ name: config.dogName, breed: config.breed, originalPhoto: photo?.dataUrl });
    if (!dog) return null;
    const fav = sortHistory(dog.history).find((h) => h.favorite);
    return fav ? { dog, entry: fav } : null;
  }, [linkedDogId, config.dogName, config.breed, photo]);

  const useFavoriteLook = () => {
    if (!favoriteLook) return;
    const { entry } = favoriteLook;
    setConfig((c) => ({
      ...c,
      style: getStyleById(entry.styleId) || (entry.styleName ? { id: entry.styleId || "custom", name: entry.styleName } : c.style),
      bodyLength: entry.bodyLength || "",
      faceStyle: entry.faceStyle || "",
      earStyle: entry.earStyle || "",
      tailStyle: entry.tailStyle || "",
      customInstructions: entry.customInstructions || "",
    }));
    toast.success(`${entry.styleName || "Favourite look"} applied`, { description: "Tap Generate Preview to see it." });
  };

  useEffect(() => {
    refreshUsage().then(setUsage);
  }, []);

  // Restore from My Dogs ("Reuse This Style" / "Try another style") or from the last session.
  useEffect(() => {
    const st = location.state;
    if (st?.dogId) {
      const dog = getDog(st.dogId);
      if (dog) {
        const entry = st.historyId ? (dog.history || []).find((h) => h.id === st.historyId) : null;
        const restored = restoreFromDog(dog, entry);
        setConfig(restored.config);
        setLinkedDogId(dog.id);
        if (restored.photo) setPhoto(restored.photo);
        if (entry) {
          toast.success(`${entry.styleName || "Style"} restored`, {
            description: restored.photo ? "Previous settings loaded. Use the saved photo or upload a new one." : "Upload a photo to continue.",
          });
        }
        navigate(location.pathname, { replace: true, state: null });
        return;
      }
    }
    const s = loadSession();
    if (s?.config) setConfig({ ...DEFAULT_CONFIG, ...s.config });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { style, customInstructions, bodyLength, faceStyle, earStyle, tailStyle, dogName, breed, breedUnknown, age } = config;
    saveSession({ config: { style, customInstructions, bodyLength, faceStyle, earStyle, tailStyle, dogName, breed, breedUnknown, age } });
  }, [config]);

  const currentStepKey = useMemo(() => {
    if (step === "photo") return "photo";
    if (step === "style") return "style";
    if (step === "generating" || step === "preview") return "preview";
    return "groomer";
  }, [step]);

  const openUpgrade = useCallback((reason) => {
    setUpgradeReason(reason || "NO_CREDITS");
    setShowUpgrade(true);
  }, []);

  const runGeneration = async () => {
    const gate = canGeneratePreview();
    if (!gate.allowed) {
      openUpgrade(gate.reason);
      return;
    }
    setStep("generating");
    try {
      const result = await generateGroomingPreview({
        originalImage: photo.dataUrl,
        breed: config.breed,
        style: config.style,
        bodyLength: config.bodyLength,
        faceStyle: config.faceStyle,
        earStyle: config.earStyle,
        tailStyle: config.tailStyle,
        customInstructions: config.customInstructions,
      });
      if (result.usage) setUsage(applyServerUsage(result.usage));
      const entry = {
        id: `r_${Date.now().toString(36)}`,
        ...result,
        style: config.style,
        settings: {
          bodyLength: config.bodyLength,
          faceStyle: config.faceStyle,
          earStyle: config.earStyle,
          tailStyle: config.tailStyle,
          customInstructions: config.customInstructions,
        },
      };
      setResults((r) => [...r, entry]);
      setActiveIdx(results.length);
      setStep("preview");
    } catch (e) {
      if (e?.code === "LIMIT_REACHED") {
        if (e.usage) setUsage(applyServerUsage(e.usage));
        setStep(results.length ? "preview" : "style");
        openUpgrade(e.reason);
        return;
      }
      toast.error(ERROR_COPY[e?.code] || ERROR_COPY.SERVER_ERROR);
      setStep(results.length ? "preview" : "style");
    }
  };

  // Persist to "My Dogs". Seam for a real backend save once auth exists.
  const saveToProfile = () => {
    const active = preview;
    if (!active) return;
    const existing =
      (linkedDogId && getDog(linkedDogId)) ||
      findDogByFingerprint({ name: config.dogName, breed: config.breed, originalPhoto: photo?.dataUrl });
    const dog = saveDog({
      id: existing?.id,
      name: config.dogName || existing?.name || "My dog",
      breed: config.breed,
      breedUnknown: config.breedUnknown,
      age: config.age,
      originalPhoto: photo.dataUrl,
    });
    const hist = addHistoryEntry(dog.id, {
      styleId: active.style?.id,
      styleName: active.style?.name,
      ...active.settings,
      previewImage: active.previewImage,
      isMock: active.isMock,
    });
    setSavedDogId(dog.id);
    setSavedEntryId(hist?.id || null);
    setLinkedDogId(dog.id);
    toast.success("Saved to My Dogs", { description: "You'll find this in your grooming history." });
    setStep("groomer");
  };

  const startOver = () => {
    setPhoto(null);
    setConfig(DEFAULT_CONFIG);
    setResults([]);
    setActiveIdx(0);
    setSavedDogId(null);
    setLinkedDogId(null);
    clearSession();
    setStep("photo");
    navigate("/");
  };

  const tryAnotherStyle = () => {
    setConfig((c) => ({ ...c, style: null }));
    setStep("style");
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]" data-testid="studio-page">
      <Header />
      <ProgressStepper current={currentStepKey} />

      <main className="mt-4">
        {step === "photo" && <UploadStep photo={photo} setPhoto={setPhoto} onContinue={() => setStep("style")} />}
        {step === "style" && (
          <StyleStep
            config={config}
            setConfig={setConfig}
            usage={usage}
            favoriteLook={favoriteLook}
            onUseFavorite={useFavoriteLook}
            onBack={() => setStep("photo")}
            onContinue={runGeneration}
          />
        )}
        {step === "generating" && <GeneratingStep styleName={config.style?.name} />}
        {step === "preview" && preview && (
          <PreviewStep
            photo={photo}
            results={results}
            activeIdx={activeIdx}
            onSelectResult={setActiveIdx}
            usage={usage}
            onTryAnotherStyle={tryAnotherStyle}
            onRegenerate={runGeneration}
            onLimit={() => openUpgrade("NO_CREDITS")}
            onSave={saveToProfile}
            onBack={() => setStep("style")}
          />
        )}
        {step === "groomer" && preview && (
          <GroomerCard
            photo={photo}
            preview={preview}
            config={{ ...config, style: preview.style, ...preview.settings }}
            savedDogId={savedDogId}
            savedEntryId={savedEntryId}
            onShareCreated={(url) => savedDogId && savedEntryId && updateHistoryEntry(savedDogId, savedEntryId, { shareUrl: url })}
            onStartOver={startOver}
            onBack={() => setStep("preview")}
          />
        )}
      </main>

      <ProUpgradeModal
        open={showUpgrade}
        reason={upgradeReason}
        onClose={() => setShowUpgrade(false)}
      />
    </div>
  );
}
